import React from "react";
import {
  formatDate,
  formatScore,
  formatTeam,
  formatRAC,
  dateWithMonthAndTimeString,
} from "../utilities/utils";
import { MDBTooltip } from "mdb-react-ui-kit";
import History from "../../constants/History";

const WagerDetailsModal = ({ selectedWager, onHide, loginPlayerInfo }) => {
  if (!selectedWager) {
    return null;
  }

  const generatePDFContent = () => {
    const title = `Ticket Number: ${selectedWager.TicketNumber || ""}`;
    const wagerGUID = `Wager GUID: ${selectedWager?.WagerGUID || ""}`;
    const pageWidth = 612;

    const calculateCenterPosition = (text, fontSize) => {
      const textLength = text.length * (fontSize / 2);
      return (pageWidth - textLength) / 2;
    };

    // Helper function to split text by fixed length
    const splitTextByLength = (text, maxLength) => {
      const lines = [];
      for (let i = 0; i < text.length; i += maxLength) {
        lines.push(text.substring(i, i + maxLength).trim());
      }
      return lines;
    };

    // Format DeleteReason and Comments
    const deleteReason = selectedWager?.DeleteReason || "";
    const comments = selectedWager?.Comments || "";

    const reasonFormatted =
      deleteReason.length > 50
        ? deleteReason.substring(0, 50)
        : deleteReason.padEnd(50, " ");

    const commentLines = splitTextByLength(comments, 50); // Split comments into lines of 50 characters

    const leftColumn = [
      `Player Account: ${loginPlayerInfo?.Player || ""} ${
        loginPlayerInfo?.TestAccount ? "(Test Account)" : ""
      }`,
      `Player Profile: ${loginPlayerInfo?.ProfileName || ""}`,
      `Line Type: ${selectedWager.LineType || ""}`,
      `Line Style: ${selectedWager.LineStyleDescription || ""}`,
      `Wager Type: ${selectedWager.WagerTypeDescription || ""}`,
      `Risk Amount: ${selectedWager.RiskAmount || 0}`,
      `Win Amount: ${selectedWager.WinAmount || 0}`,
    ];

    const rightColumn = [
      `Placed Date: ${formatDate(selectedWager.PlacedDate) || ""}`,
      `Graded Date: ${formatDate(selectedWager.LastModification) || ""}`,
      `Settled Date: ${formatDate(selectedWager.SettledDate) || ""}`,
      `Last Modification: ${formatDate(selectedWager.LastModification) || ""}`,
      `Tax Amount: ${selectedWager.TaxAmount || 0}`,
      `Points Purchased: ${selectedWager.PointsPurchased || 0}`,
      `Cashout Amount: ${selectedWager.CashoutAmount || 0}`,
      `Cashout Fee: ${selectedWager.CashoutFee || 0}`,
    ];

    const fullWidthContent = [
      `Wager deleted: ${selectedWager.ResultDescription || "No"}`,
      `Reason: ${reasonFormatted}`,
      `Comments:`,
      ...commentLines,
    ];

    let pdfContent = `%PDF-1.4\n1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n`;
    pdfContent += `2 0 obj\n<< /Type /Pages /Count 1 /Kids [3 0 R] >>\nendobj\n`;
    pdfContent += `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R >>\nendobj\n`;
    pdfContent += `4 0 obj\n<< /Length 5 0 R >>\nstream\n`;

    // Add title and wager GUID
    pdfContent += `BT\n/F1 16 Tf\n${calculateCenterPosition(
      title,
      16,
    )} 770 Td\n(${title}) Tj\nET\n`;
    pdfContent += `BT\n/F1 12 Tf\n${calculateCenterPosition(
      wagerGUID,
      12,
    )} 750 Td\n(${wagerGUID}) Tj\nET\n`;

    let yLeft = 720;
    let yRight = 720;
    const lineHeight = 15;

    // Left column content
    leftColumn.forEach((line) => {
      if (line.trim()) {
        pdfContent += `BT\n/F1 10 Tf\n72 ${yLeft} Td\n(${line
          .replace(/\(/g, "\\(")
          .replace(/\)/g, "\\)")}) Tj\nET\n`;
      }
      yLeft -= lineHeight;
    });

    // Right column content
    rightColumn.forEach((line) => {
      if (line.trim()) {
        pdfContent += `BT\n/F1 10 Tf\n300 ${yRight} Td\n(${line
          .replace(/\(/g, "\\(")
          .replace(/\)/g, "\\)")}) Tj\nET\n`;
      }
      yRight -= lineHeight;
    });

    // Full-width content (comments and additional lines)
    let yFullWidth = Math.min(yLeft, yRight) - 30;
    fullWidthContent.forEach((line) => {
      if (line.trim()) {
        pdfContent += `BT\n/F1 10 Tf\n72 ${yFullWidth} Td\n(${line
          .replace(/\\/g, "\\\\")
          .replace(/\(/g, "\\(")
          .replace(/\)/g, "\\)")}) Tj\nET\n`;
        yFullWidth -= lineHeight; // Adjust Y position for each line
      }
    });

    pdfContent += `endstream\nendobj\n5 0 obj\n${
      (leftColumn.length + rightColumn.length + fullWidthContent.length) *
      lineHeight
    }\nendobj\nxref\n0 6\n`;
    pdfContent += `0000000000 65535 f\n0000000010 00000 n\n0000000073 00000 n\n0000000178 00000 n\n`;
    pdfContent += `0000000289 00000 n\n0000000476 00000 n\ntrailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n574\n%%EOF`;

    return pdfContent;
  };

  const saveToPDF = async () => {
    const pdfContent = generatePDFContent();

    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: `WagerDetails_${
          selectedWager.TicketNumber || "ticket"
        }.pdf`,
        types: [
          {
            description: "PDF Files",
            accept: { "application/pdf": [".pdf"] },
          },
        ],
      });

      const writable = await handle.createWritable();
      await writable.write(new Blob([pdfContent], { type: "application/pdf" }));
      await writable.close();
    } catch (err) {
      return;
    }
  };

  return (
    <div
      className="modal fade show"
      tabIndex="-1"
      role="dialog"
      data-backdrop="static"
      style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div className="modal-dialog custom-modal-width-Wager" role="document">
        <div className="modal-content">
          {/* Header START*/}
          <div
            className="modal-header headerModals"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <h5 className="modal-title">
              Wager Details for Ticket # {selectedWager.TicketNumber}
            </h5>
            <button
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
              onClick={onHide}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          {/* Header END*/}

          <div className="modal-body">
            {/* Fieldset for Wager Description START*/}
            <fieldset
              style={{
                backgroundColor: "rgb(217, 230, 242, 0.7)",
                borderRadius: "10px",
                padding: "20px",
                margin: "2px",
                position: "relative",
              }}
            >
              <legend
                style={{
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  color: "#005580",
                  padding: "10px",
                  position: "relative",
                  top: "20px",
                  right: "10px",
                }}
              >
                Wager Description
              </legend>
              <div className="row">
                <div className="col-sm-12">
                  <div className="mt-1">
                    <strong>{selectedWager.CompleteDescription} </strong>
                  </div>
                  <div className="my-2 mt-3">
                    <strong>Wager GUID: </strong>
                    <span>{selectedWager?.WagerGUID || ""}</span>{" "}
                    {selectedWager?.IDProduct === History.IDProductCB && (
                      <span
                        style={{
                          borderRadius: "15px",
                          fontSize: "0.75rem",
                          color: "#fff",
                          fontWeight: "normal",
                        }}
                        className="badge bg-info px-2"
                      >
                        Chat Bet
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </fieldset>
            {/* Fieldset for Wager Description END*/}

            {/* Wager Info START */}
            <div className="mx-4 my-3" style={{ whiteSpace: "nowrap" }}>
              <div className="row">
                {/* Column 1 */}
                <div className="col-md-2 mr-5">
                  <div className="row">
                    <div className="col-6 wager-details-label">
                      Player Account:
                    </div>
                    <div className="col-6">
                      {loginPlayerInfo?.Player || ""}
                      {loginPlayerInfo?.TestAccount && (
                        <span style={{ color: "skyblue" }}>
                          {" "}
                          (Test Account)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 wager-details-label">
                      Player Profile:
                    </div>
                    <div className="col-6">
                      {loginPlayerInfo.ProfileName || ""}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 wager-details-label">Line Type:</div>
                    <div className="col-6">{selectedWager.LineType || ""}</div>
                  </div>

                  <div className="row">
                    <div className="col-6 wager-details-label">Line Style:</div>
                    <div className="col-6">
                      {selectedWager.LineStyleDescription || ""}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 wager-details-label">Wager Type:</div>
                    <div className="col-6">
                      {selectedWager.WagerTypeDescription || ""}
                    </div>
                  </div>
                </div>

                {/* Column 2 */}
                <div className="col-md-2 mr-5">
                  <div className="row">
                    <div className="col-4 mr-3 wager-details-label">
                      Risk Amount:
                    </div>
                    <div className="col-6">
                      {selectedWager.RiskAmount || ""}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 mr-3 wager-details-label mr-3">
                      Win Amount:
                    </div>
                    <div className="col-6">{selectedWager.WinAmount || ""}</div>
                  </div>

                  <div className="row">
                    <div className="col-4 mr-3 wager-details-label mr-3">
                      Placed Date:
                    </div>
                    <div className="col-6">
                      {dateWithMonthAndTimeString(
                        selectedWager.PlacedDate || "",
                      )}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 mr-3 wager-details-label mr-3">
                      Graded Date:
                    </div>
                    <div className="col-6">
                      {dateWithMonthAndTimeString(
                        selectedWager.LastModification,
                      ) || ""}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-4 mr-3 wager-details-label mr-3">
                      Settled Date:
                    </div>
                    <div className="col-6">
                      {dateWithMonthAndTimeString(selectedWager.SettledDate) ||
                        ""}
                    </div>
                  </div>
                </div>

                {/* Column 3 */}
                <div className="col-md-2 mr-4">
                  <div className="row">
                    <div className="col-6 mr-5 wager-details-label">
                      Original Wager Amount:
                    </div>
                    <div className="col-3">
                      {selectedWager.OriginalWagerAmounth || ""}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 mr-5 wager-details-label">
                      Original Risk Amount:
                    </div>
                    <div className="col-3">
                      {selectedWager.OriginalRiskAmount || "+"}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 mr-5 wager-details-label">
                      Original Win Amount:
                    </div>
                    <div className="col-3">
                      {selectedWager.OriginalWinAmount || ""}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 mr-5 wager-details-label">
                      Tax Amount:
                    </div>
                    <div className="col-3">{selectedWager.TaxAmount || 0}</div>
                  </div>

                  <div className="row">
                    <div className="col-6 mr-5 wager-details-label">
                      Points Purchased:
                    </div>
                    <div className="col-3">
                      {selectedWager.PointsPurchased || 0}
                    </div>
                  </div>
                </div>
                {/* Column 4 */}
                <div className="col-md-2">
                  <div className="row">
                    <div className="col-6 mr-2 wager-details-label">
                      Cashout Amount:
                    </div>
                    <div className="col-5">
                      {selectedWager.CashoutAmount || 0}
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-6 mr-2 wager-details-label">
                      Cashout Fee:
                    </div>
                    <div className="col-5">{selectedWager.CashoutFee || 0}</div>
                  </div>

                  <div className="row">
                    <div className="col-6 mr-2 wager-details-label">
                      Last Modification:
                    </div>
                    <div className="col-5">
                      {dateWithMonthAndTimeString(
                        selectedWager.LastModification,
                      ) || ""}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Wager Info END */}

            {/* Fieldset for Wager deleted START*/}
            <fieldset
              style={{
                backgroundColor: "rgba(191, 191, 191, 0.23)",
                borderRadius: "10px",
                padding: "20px",
                margin: "1px",
                position: "relative",
              }}
            >
              <legend
                style={{
                  fontSize: "1rem",
                  padding: "10px",
                  position: "relative",
                  top: "20px",
                  right: "10px",
                }}
              >
                Wager Deleted: <span style={{ fontWeight: "bold" }}>No</span>
              </legend>
              <div className="row">
                <div className="col-6">
                  <label htmlFor="reason" className="form-label">
                    Reason
                  </label>
                  <textarea
                    id="reason"
                    className="form-control"
                    rows="3"
                    disabled
                  ></textarea>
                </div>
                <div className="col-6">
                  <label htmlFor="comments" className="form-label">
                    Comments
                  </label>
                  <textarea
                    id="comments"
                    className="form-control"
                    rows="3"
                    disabled
                  ></textarea>
                </div>
              </div>
            </fieldset>
            {/* Fieldset for Wager deleted END*/}

            {/* Bet Detials info START */}
            <div className="row">
              <div className="col-sm-12">
                <div className="divTables" style={{ overflowX: "auto" }}>
                  <table className="table table-striped table-sm">
                    <thead className="table-primary">
                      <tr className="text-center">
                        <th>Detail</th>
                        <th>Status</th>
                        <th>Ln Factor</th>
                        <th>Game Date</th>
                        <th>Sport/League</th>
                        <th>Teams</th>
                        <th>Score</th>
                        <th>Derivate Pts</th>
                        <th>Real Sport</th>
                        <th>Game UUID</th>
                        <th>Points</th>
                        <th>Odds</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedWager.Details &&
                      selectedWager.Details.length > 0 ? (
                        selectedWager.Details.map((detail, index) => (
                          <tr key={index} className="text-center">
                            <td>
                              {detail.IsSGP && (
                                <span
                                  style={{
                                    borderRadius: "20px",
                                    fontSize: "0.75rem",
                                    color: "#fff",
                                    marginRight: "10px",
                                  }}
                                  class="badge bg-primary"
                                >
                                  SGP
                                </span>
                              )}
                              {detail.IsEarlyPO && (
                                <MDBTooltip
                                  tag="span"
                                  wrapperProps={{ href: "#" }}
                                  title="Early Payout"
                                >
                                  <span
                                    style={{
                                      borderRadius: "20px",
                                      fontSize: "0.75rem",
                                      color: "#fff",
                                      marginRight: "10px",
                                    }}
                                    class="badge bg-success"
                                  >
                                    EP
                                  </span>
                                </MDBTooltip>
                              )}
                              {detail?.CompleteDescription || ""}
                            </td>
                            <td>{detail?.DetailResultDescription || ""}</td>
                            <td>{detail?.LineFactor || ""}</td>
                            <td>
                              {dateWithMonthAndTimeString(
                                detail?.GameDateTime,
                              ) || ""}
                            </td>
                            <td>{formatRAC(detail)}</td>
                            <td>{formatTeam(detail)}</td>
                            <td>{formatScore(detail)}</td>
                            <td>{detail?.DerivativePoints || 0}</td>
                            <td>{detail?.RealSport || ""}</td>
                            <td>{detail?.UUID || ""}</td>
                            <td>{detail?.CSPoints || ""}</td>
                            <td>{detail?.CSOdds || ""}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "center" }}>
                            No details available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {/* Bet Detials info END */}

            {/* Fieldset for Detail Voided START*/}
            <fieldset
              style={{
                backgroundColor: "rgba(191, 191, 191, 0.23)",
                borderRadius: "10px",
                padding: "20px",
                margin: "1px",
                position: "relative",
              }}
            >
              <legend
                style={{
                  fontSize: "1rem",
                  padding: "10px",
                  position: "relative",
                  top: "20px",
                  right: "10px",
                }}
              >
                Detail Voided: <span style={{ fontWeight: "bold" }}>No</span>
              </legend>
              <div className="row">
                <div className="col-6">
                  <label htmlFor="reason" className="form-label">
                    Reason
                  </label>
                  <textarea
                    id="reason"
                    className="form-control"
                    rows="3"
                    disabled
                  ></textarea>
                </div>
                <div className="col-6">
                  <label htmlFor="comments" className="form-label">
                    Comments
                  </label>
                  <textarea
                    id="comments"
                    className="form-control"
                    rows="3"
                    disabled
                  ></textarea>
                </div>
              </div>
            </fieldset>
            {/* Fieldset for Detail Voided END*/}
          </div>

          <div
            className="modal-footer"
            style={{
              position: "sticky",
              bottom: 0,
              zIndex: 10,
              backgroundColor: "#fff",
              borderTop: "1px solid #dee2e6",
            }}
          >
            <button
              type="button"
              className="btn btn-primary"
              onClick={saveToPDF}
            >
              Download
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              data-dismiss="modal"
              onClick={onHide}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WagerDetailsModal;
